import type { InputSource, ResponseCode, ResponseMode } from '@/domain/core/types';

export interface RawResponse {
  code: ResponseCode;
  /** event.timeStamp — performance.now() 와 같은 시간원점. 핸들러 큐잉 지연이 빠져 있다. */
  ts: number;
  handlerDelayMs: number;
  source: InputSource;
}

export interface KeyMap { top: string[]; bottom: string[]; left: string[]; right: string[] }

export const DEFAULT_KEYS: KeyMap = {
  top: ['ArrowUp', 'KeyE', 'e', 'E', 'ㄷ'],
  bottom: ['ArrowDown', 'KeyC', 'c', 'C', 'ㅊ'],
  left: ['ArrowLeft', 'z', 'Z', 'ㅋ'],
  right: ['ArrowRight', 'm', 'M', 'ㅡ'],
};

/**
 * 키보드·포인터를 하나의 반응 스트림으로 합친다.
 *
 * - 리스너는 항상 붙어 있고 (arm/disarm 으로 붙였다 뗐다 하지 않는다 — 그 자체가 지연이다)
 *   Runner 가 `armed` 를 켜고 끈다.
 * - 키보드는 capture:true로 받고 해당 축의 키에서 기본 스크롤을 막는다.
 * - 포인터는 passive:true이며 스크롤·줌 차단은 캔버스 CSS가 맡는다.
 * - 터치 히트존은 DOM 이 아니라 캔버스 좌표 판정. 별도 요소를 두면 히트테스트 비용이 붙는다.
 * - 핸들러 안에서 객체를 하나 만든다(RawResponse). 트라이얼당 1회라 허용한다.
 */
export class InputCollector {
  #canvas: HTMLCanvasElement;
  #keys: KeyMap;
  #mode: ResponseMode;
  #armed = false;
  #onResponse: ((r: RawResponse) => void) | null = null;
  #onPremature: (() => void) | null = null;
  #keyToCode = new Map<string, ResponseCode>();

  constructor(canvas: HTMLCanvasElement, mode: ResponseMode, keys: KeyMap = DEFAULT_KEYS) {
    this.#canvas = canvas;
    this.#mode = mode;
    this.#keys = keys;
    this.#rebuildKeyMap();
    window.addEventListener('keydown', this.#onKey, { capture: true });
    canvas.addEventListener('pointerdown', this.#onPointer, { capture: true, passive: true });
  }

  #rebuildKeyMap(): void {
    this.#keyToCode.clear();
    (Object.keys(this.#keys) as ResponseCode[]).forEach((code) => {
      for (const k of this.#keys[code]) this.#keyToCode.set(k, code);
    });
  }

  arm(onResponse: (r: RawResponse) => void, onPremature: () => void): void {
    this.#onResponse = onResponse;
    this.#onPremature = onPremature;
    this.#armed = true;
  }
  disarm(onPremature: (() => void) | null = null): void { this.#armed = false; this.#onResponse = null; this.#onPremature = onPremature; }

  #emit(code: ResponseCode, ts: number, source: InputSource): void {
    // 반응 모드에 맞지 않는 축의 키는 무시 (vertical 인데 ArrowLeft 등)
    const ok = this.#mode === 'vertical' ? (code === 'top' || code === 'bottom') : (code === 'left' || code === 'right');
    if (!ok) return;
    if (!this.#armed) { this.#onPremature?.(); return; }
    this.#armed = false;
    this.#onPremature = null;
    this.#onResponse?.({ code, ts, handlerDelayMs: performance.now() - ts, source });
  }

  #onKey = (e: KeyboardEvent): void => {
    const code = this.#keyToCode.get(e.code) ?? this.#keyToCode.get(e.key);
    if (!code) return;
    const relevant = this.#mode === 'vertical' ? code === 'top' || code === 'bottom' : code === 'left' || code === 'right';
    if (!relevant) return;
    e.preventDefault();
    if (!e.repeat) this.#emit(code, e.timeStamp, 'keyboard');
  };

  #onPointer = (e: PointerEvent): void => {
    const r = this.#canvas.getBoundingClientRect();
    if (this.#mode === 'vertical') {
      const code: ResponseCode = e.clientY - r.top < r.height / 2 ? 'top' : 'bottom';
      this.#emit(code, e.timeStamp, 'pointer');
    } else {
      const code: ResponseCode = e.clientX - r.left < r.width / 2 ? 'left' : 'right';
      this.#emit(code, e.timeStamp, 'pointer');
    }
  };

  dispose(): void {
    window.removeEventListener('keydown', this.#onKey, { capture: true });
    this.#canvas.removeEventListener('pointerdown', this.#onPointer, { capture: true });
    this.#armed = false;
  }
}
